import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { enableProdMode, LOCALE_ID, ErrorHandler, importProvidersFrom, DEFAULT_CURRENCY_CODE } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { NgxFormsErrorsConfigurationToken } from '@studiohyperdrive/ngx-forms';
import { NgxI18nService, NgxI18nModule } from '@studiohyperdrive/ngx-i18n';
import { provideNgxModalConfiguration, provideNgxTooltipConfiguration } from '@studiohyperdrive/ngx-inform';
import { provideNgxDisplayContentConfiguration, provideNgxDragAndDropService } from '@studiohyperdrive/ngx-layout';
import { NgxTableConfigToken } from '@studiohyperdrive/ngx-table';
import { provideNgxTourConfiguration } from '@studiohyperdrive/ngx-tour';
import { provideNgxReplaceElementsConfiguration } from '@studiohyperdrive/ngx-utils';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { provideMatomo } from 'ngx-matomo-client';

import { provideEloketCoreData, VlaioFormErrorComponent } from '@vlaio/e-loket/core';
import { provideNotificationsData } from '@vlaio/e-loket/notifications';
import { providePartnersData } from '@vlaio/e-loket/partners/shared';
import { httpInterceptors } from '@vlaio/shared/authentication';
import { provideCompanyData } from '@vlaio/shared/company';
import { ErrorService, provideSharedCoreData, VlaioDragAndDropService } from '@vlaio/shared/core';
import { sharedI18nKeys } from '@vlaio/shared/i18n';
import { providePagesData } from '@vlaio/shared/page';
import { provideProductData } from '@vlaio/shared/products';
import { provideTrackingData } from '@vlaio/shared/tracking';
import { AbstractNgxI18nService } from '@vlaio/shared/types';
import {
	VlaioErrorComponent,
	VlaioLoaderComponent,
	VlaioOfflineComponent,
	VlaioTooltipComponent
} from '@vlaio/shared/ui/common';
import { provideSharedUiFormsData } from '@vlaio/shared/ui/forms';
import { VlaioActionModalComponent, VlaioLoadingModalComponent } from '@vlaio/shared/ui/modal';
import { VlaioTourStepComponent } from '@vlaio/shared/ui/tour';
import { provideUserData } from '@vlaio/shared/user';
import { provideSharedUtilsData } from '@vlaio/shared/utils';
import { environment } from '~environment';

import { AppComponent } from './app/app.component';
import { routing } from './app/app.routing';
import { ELoketTranslationLoader } from './app/translation.loader';

import './polyfills';

if (environment.production) {
	enableProdMode();
	window.console.log = () => null;
}

bootstrapApplication(AppComponent, {
	providers: [
		providePagesData(),
		provideUserData(),
		provideSharedCoreData(),
		provideEloketCoreData(),
		provideSharedUtilsData(),
		provideCompanyData(),
		provideProductData(),
		provideTrackingData(),
		providePartnersData(),
		provideNotificationsData(),
		provideSharedUiFormsData(),
		importProvidersFrom(
			BrowserModule,
			ReactiveFormsModule,
			routing,
			MatSnackBarModule,
			NgxI18nModule.forRoot(
				{
					defaultLanguage: 'nl',
					defaultAssetPaths: ['./assets/i18n/shared/i18n/'],
					// Wouter: Explicitly cast to string to avoid Bamboo replacement type error
					cacheBust: String(environment.cacheBust)
				},
				ELoketTranslationLoader
			),
			StoreModule.forRoot([]),
			StoreDevtoolsModule.instrument({
				maxAge: 25
			})
		),
		{
			provide: LOCALE_ID,
			useValue: 'nl-BE'
		},
		{
			provide: ErrorHandler,
			useClass: ErrorService
		},
		{
			provide: DATE_PIPE_DEFAULT_OPTIONS,
			useValue: { dateFormat: 'dd/MM/yyyy' }
		},
		{
			provide: AbstractNgxI18nService,
			useClass: NgxI18nService
		},
		{
			provide: NgxFormsErrorsConfigurationToken,
			useValue: {
				showWhen: 'touched',
				errors: {
					invalidINSZ: sharedI18nKeys.FormErrors.InvalidInsz,
					required: sharedI18nKeys.FormErrors.Required,
					incorrectChronologicalDates: sharedI18nKeys.FormErrors.ChronologicalDates,
					incorrectChronologicalDate: sharedI18nKeys.FormErrors.ChronologicalDates,
					invalidMandateExecutorRuntime: sharedI18nKeys.FormErrors.MandateExecutorRuntime,
					minlength: sharedI18nKeys.FormErrors.MinLength,
					invalidMinimumDate: sharedI18nKeys.FormErrors.MinDate
				},
				component: VlaioFormErrorComponent
			}
		},
		{
			provide: NgxTableConfigToken,
			useValue: {
				showOpenRowState: true,
				ngxTableClass: 'vlaio-table',
				showDetailRow: 'on-single-item',
				emitValueOnSingleItem: true
			}
		},
		provideEnvironmentNgxMask(),
		provideHttpClient(withInterceptors(httpInterceptors)),
		provideAnimations(),
		provideNgxTourConfiguration({ component: VlaioTourStepComponent, offset: { top: 43 } }),
		provideNgxTooltipConfiguration({
			component: VlaioTooltipComponent
		}),
		provideNgxReplaceElementsConfiguration({
			internalLink: {
				element: 'vlaio-internal-translated-link',
				selector: 'a[data-internal-link-id={{id}}]',
				includeInnerText: true
			},
			externalLink: {
				element: 'vlaio-external-translated-link',
				selector: 'a[data-external-link-id={{id}}]',
				includeInnerText: true
			}
		}),
		provideNgxModalConfiguration({
			closeOnNavigation: true,
			autoClose: true,
			modals: {
				action: {
					component: VlaioActionModalComponent,
					role: 'dialog'
				},
				loading: {
					component: VlaioLoadingModalComponent,
					role: 'dialog',
					autoClose: false,
					hasBackdrop: true
				}
			}
		}),
		provideNgxDisplayContentConfiguration({
			components: {
				error: VlaioErrorComponent,
				loading: VlaioLoaderComponent,
				offline: VlaioOfflineComponent
			},
			listenToOnlineStatus: true
		}),
		provideMatomo({
			trackerUrl: 'https://eloketondernemers.matomo.cloud/',
			siteId: environment.matomo.siteId
		}),
		provideNgxDragAndDropService(VlaioDragAndDropService),
		{ provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' }
	]
}).catch((err) => console.error(err));
